import React from 'react'

import {
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  Legend as RechartsLegend,
  Cell,
  Label,
} from 'recharts'

import { Stack, Box, Typography } from '@pnld/components-web'

import { COLORS, Legend } from '../common'
import {
  PieChartProps,
  LegendPosition,
  ChartData,
  CustomTooltipProps,
} from './types'

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0]
    const { name, value } = data.payload

    if (data.payload._empty) {
      return null
    }

    const formattedValue = new Intl.NumberFormat('pt-BR').format(value)

    return (
      <Stack
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        boxShadow="sm"
        padding={3}
      >
        <Typography
          variant="s-xs"
          color="gray.700"
          sx={{ lineHeight: 1, fontWeight: 'bold' }}
        >
          {name}
        </Typography>
        <Typography
          variant="b-sm"
          color="blue.600"
          sx={{ lineHeight: 1, fontWeight: 500 }}
        >
          {formattedValue}
        </Typography>
      </Stack>
    )
  }

  return null
}

const CenterLabel = ({
  viewBox,
  content,
  boxSize = 140,
}: {
  viewBox?: { cx?: number; cy?: number }
  content: React.ReactNode
  boxSize?: number
}) => {
  const cx = viewBox?.cx ?? 0
  const cy = viewBox?.cy ?? 0
  const half = boxSize / 2

  return (
    <g pointerEvents="none">
      <foreignObject
        x={cx - half}
        y={cy - half}
        width={boxSize}
        height={boxSize}
        requiredExtensions="http://www.w3.org/1999/xhtml"
      >
        <Box
          width={boxSize}
          height={boxSize}
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          color="brand.primary.dark_2"
          fontSize={14}
          fontWeight={700}
          overflow="hidden"
          wordBreak="break-word"
        >
          {React.isValidElement(content) ? (
            content
          ) : (
            <>{String(content ?? '')}</>
          )}
        </Box>
      </foreignObject>
    </g>
  )
}

const CustomPieChart = ({
  data,
  centralLabel,
  legendDirection = 'column',
  chartHeight = 260,
  chartMinWidth = 500,
  innerRadius = 80,
  outerRadius = 110,
  centerBox = 140,
  rightGutter = 250,
  startAngle = 90,
  endAngle = -270,
  isAnimationActive = false,
}: PieChartProps) => {
  const legendPosition: Record<'row' | 'column', LegendPosition> = {
    row: { layout: 'horizontal', align: 'center', verticalAlign: 'bottom' },
    column: { layout: 'vertical', align: 'right', verticalAlign: 'middle' },
  }

  const needsRightGutter = legendDirection === 'column'

  const hasEmptyData =
    Array.isArray(data) && data.every(d => (d?.value ?? 0) === 0)

  const normalizedData = hasEmptyData
    ? [...data, { name: '__empty__', value: 1, _empty: true }]
    : data

  const shouldShowLegend = !hasEmptyData && !normalizedData.some(d => d._empty)

  return (
    <Box
      position="relative"
      height={chartHeight}
      width="100%"
      minWidth={chartMinWidth}
      paddingRight={needsRightGutter && shouldShowLegend ? rightGutter : 0}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart
          margin={{
            top: 0,
            right: 0,
            bottom: legendDirection === 'row' ? 60 : 0,
            left: 0,
          }}
        >
          {shouldShowLegend && (
            <>
              <Tooltip content={<CustomTooltip />} />
              <RechartsLegend
                content={<Legend direction={legendDirection} />}
                {...legendPosition[legendDirection]}
                wrapperStyle={
                  legendDirection === 'column'
                    ? {
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translate(100%, -50%)',
                        width: 0,
                        textWrap: 'nowrap',
                      }
                    : {
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: '100%',
                        paddingTop: '8px',
                      }
                }
              />
            </>
          )}

          <Pie
            data={normalizedData}
            dataKey="value"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            isAnimationActive={isAnimationActive}
            startAngle={startAngle}
            endAngle={endAngle}
          >
            <Label
              position="center"
              content={
                <CenterLabel content={centralLabel} boxSize={centerBox} />
              }
            />
            {normalizedData.map((entry: ChartData, index: number) => (
              <Cell
                key={index}
                fill={
                  entry?._empty
                    ? 'var(--chakra-colors-gray-200)'
                    : COLORS[index % COLORS.length]
                }
                style={{ outline: 'none' }}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default CustomPieChart
